/* ============================================================================
 * Gestão de Documentos Fiscais — NFS-e
 *
 * Portado de base-html `erpone_gest_o_de_documentos_fiscais_nfs_e`:
 *   Status | Number | Competence Date | Service Description | Customer.
 *
 * Contrato real consumido (fiscal-documents.dto.ts):
 *   GET /fiscal/invoices?type=NFSE&status=&page=&limit=
 *     → PaginatedFiscalDocumentListDto (data[] FiscalDocumentListItemDto)
 *   GET /fiscal/invoices/:id → FiscalDocumentDetailDto (modal de detalhe)
 *
 * O protótipo mostra colunas "Municipality" e "ISS (R$)" que o DTO não
 * publica (não há município nem ISS separado em FiscalDocumentListItemDto —
 * só `totalValue`). Essas colunas foram omitidas; Valor Total sai do dado real.
 * Os KPIs "Autorizadas (Mês) / Rejeitadas / Processando / Total ISS Retido"
 * também não existem nesta API (a resposta não traz `counts`) — omitidos.
 * ========================================================================== */

import { useState } from 'react';
import { useAuth } from '../../../shared/auth/AuthContext';
import { formatCurrency, formatDate, EMPTY } from '../../../shared/utils/format';
import { Badge, Button, LoadingSkeleton } from '@nexora/design-system';
import { useFiscalDocuments } from './fiscalDocumentsHooks';
import {
  documentStatusLabel,
  documentStatusVariant,
  documentStatusIcon,
} from './fiscalStatusMaps';
import { FISCAL_DOCUMENTS_CSS } from './fiscalDocumentsCss';
import { FiscalDocumentDetailModal } from './FiscalDocumentDetailModal';

const STATUS_FILTERS = ['ALL', 'AUTHORIZED', 'PROCESSING', 'REJECTED', 'DENIED', 'CANCELLED', 'DRAFT', 'QUEUED'];

const PAGE_SIZE = 20;

export function NfseDocumentsPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';

  const [statusFilter, setStatusFilter] = useState('ALL');
  const [page, setPage] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useFiscalDocuments(tenantId, {
    type: 'NFSE',
    status: statusFilter,
    page: page + 1,
    limit: PAGE_SIZE,
  });

  const documents = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="fiscal-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">
            Gestão de Documentos Fiscais (NFS-e)
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Notas fiscais de serviço eletrônicas e seu status junto ao município.
          </p>
        </div>
      </div>

      {/* ── Filtros ── */}
      <div className="fiscal-toolbar">
        <select
          className="fiscal-control fiscal-control--select"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(0);
          }}
          aria-label="Filtrar por status"
        >
          <option value="ALL">Todos os status</option>
          {STATUS_FILTERS.filter((s) => s !== 'ALL').map((s) => (
            <option key={s} value={s}>
              {documentStatusLabel(s)}
            </option>
          ))}
        </select>
        <span className="fiscal-count">
          {total} {total === 1 ? 'documento' : 'documentos'}
        </span>
      </div>

      {/* ── Tabela ── */}
      <div className="fiscal-card">
        {isLoading && (
          <div style={{ padding: '1rem' }}>
            <LoadingSkeleton count={6} height="40px" />
          </div>
        )}

        {error && !isLoading && (
          <div className="fiscal-state">
            <p className="fiscal-state__title">Erro ao carregar NFS-e</p>
            <p className="fiscal-state__detail">{(error as Error).message}</p>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              Tentar novamente
            </Button>
          </div>
        )}

        {!isLoading && !error && documents.length === 0 && (
          <div className="fiscal-state">
            <p className="fiscal-state__detail">
              Nenhuma NFS-e encontrada{statusFilter !== 'ALL' ? ' com o filtro atual' : ''}.
            </p>
          </div>
        )}

        {!isLoading && !error && documents.length > 0 && (
          <>
            <div className="fiscal-table">
              <table>
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Número</th>
                    <th>Competência</th>
                    <th>Serviço prestado</th>
                    <th>Cliente</th>
                    <th style={{ textAlign: 'right' }}>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr key={doc.id} onClick={() => setSelectedId(doc.id)}>
                      <td>
                        <Badge variant={documentStatusVariant(doc.status)} size="sm">
                          <span
                            className="material-symbols-outlined fiscal-status-icon"
                            aria-hidden="true"
                          >
                            {documentStatusIcon(doc.status)}
                          </span>
                          {documentStatusLabel(doc.status)}
                        </Badge>
                      </td>
                      <td className="fiscal-mono">
                        {doc.documentNumber
                          ? `${String(doc.documentNumber).padStart(6, '0')}${doc.serie ? ` / ${doc.serie}` : ''}`
                          : EMPTY}
                      </td>
                      <td className="fiscal-nowrap">{formatDate(doc.competenceDate)}</td>
                      <td>{doc.serviceDescription ?? EMPTY}</td>
                      <td>
                        {doc.customerName ? (
                          <span className="fiscal-recipient">{doc.customerName}</span>
                        ) : (
                          EMPTY
                        )}
                      </td>
                      <td className="fiscal-num">{formatCurrency(doc.totalValue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="fiscal-footer">
              <span className="font-body-sm text-body-sm text-on-surface-variant tabular-nums">
                {page * PAGE_SIZE + 1}–{Math.min(total, page * PAGE_SIZE + documents.length)} de{' '}
                {total}
              </span>
              <div className="flex gap-xs">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Anterior
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page + 1 >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Próxima
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      <FiscalDocumentDetailModal
        documentId={selectedId}
        onClose={() => setSelectedId(null)}
      />

      <style>{FISCAL_DOCUMENTS_CSS}</style>
    </div>
  );
}